import { ArrowRight, ExternalLink, AlertCircle, Wrench } from 'lucide-react';

import { Link } from 'react-router-dom';
import { STATUS_COLORS } from './colors';

const OPEN_BUGS_JQL_URL =
  'https://contentsquare.atlassian.net/issues/?jql=labels%20%3D%20%22shield-qa%22%20AND%20resolution%20%3D%20Unresolved';

const OPEN_FALSE_POSITIVES_JQL_URL =
  'https://contentsquare.atlassian.net/issues/?jql=labels%20%3D%20%22shield-improvement%22%20AND%20resolution%20%3D%20Unresolved';

const formatPct = (v) => (v === null || v === undefined ? '—' : `${Number.isInteger(v) ? v : v.toFixed(1)}%`);

// `rates` (pass/fail/warning) come from the existing /dashboard call; `kpis` (silent prompts, bugs,
// false positives) from /summary-insights and may be missing if that call fails.
// "X%" card with its sample size underneath, or "—" plus a hint when there is nothing to divide.
// Health colour for a 0-100 score: below 40 red, 40-70 orange, above 70 green.
export function scoreColor(pct) {
  if (pct === null || pct === undefined) return undefined;
  if (pct < 40) return STATUS_COLORS.fail;
  if (pct <= 50) return STATUS_COLORS.warning;
  return STATUS_COLORS.passed;
}

function shareTile({ label, part, total, sub, hint, title, colored = false, isMaintenance = false }) {
  const pct = total > 0 ? Math.round((part / total) * 1000) / 10 : null;
  return {
    label,
    title,
    isMaintenance,
    color: colored ? scoreColor(pct) : undefined,
    value: pct !== null ? formatPct(pct) : '—',
    sub: total > 0 ? sub(part, total) : undefined,
    hint,
  };
}

function reviewedTile(reviewedPrompts, verdicts) {
  const { reviewed, total, isMaintenance = false } = reviewedPrompts || {};

  return {
    label: 'Reviewed issues',
    value: total > 0 ? formatPct(Math.round((reviewed / total) * 1000) / 10) : '—',
    sub: total > 0
      ? `${reviewed} of ${total} issues (prompt + error) judged` +
        (verdicts ? ` · ${verdicts.accurate} accurate / ${verdicts.notAccurate} not accurate` : '')
      : undefined,
    isMaintenance,
    hint: isMaintenance 
      ? 'System under maintenance' 
      : 'No failures or warnings to review in this range',
    title: 'An issue is one prompt failing with one error, however many runs it repeats over. One review covers all of them.',
  };
}



function getHealthBadge(pct) {
  if (pct === null || pct === undefined) return null;
  if (pct >= 85) return 'Excellent';
  if (pct >= 70) return 'Good';
  if (pct >= 50) return 'Fair';
  return 'Critical';
}

// Fail/warning rates: higher is worse, the opposite of the success tiles.
function getBadRateBadge(pct) {
  if (pct === null || pct === undefined) return null;
  if (pct >= 60) return 'Critical';
  if (pct >= 30) return 'Alert';
  return 'Excellent';
}

function KpiRow({ rates, kpis }) {
  const accuracyTiles = kpis && kpis.senseAnalystSuccess && kpis.falsePositiveShare && kpis.falsePositives ? [
    shareTile({
      label: 'Sense Analyst success',
      colored: true,
      isMaintenance: false,
      part: kpis.senseAnalystSuccess.succeeded || 0,
      total: kpis.senseAnalystSuccess.total || 0,
      sub: (part, total) =>
        `Successful prompts ${part} on total ${total}`,
      hint: 'No executions in this range',
      title:
        'Passed prompts, plus failed prompts whose issue (prompt + error) reviewers judged "not accurate" — i.e. Pass rate without Shield\'s false positives. Warnings are excluded from this metric. Unreviewed failures still count as failures.',
    }),
    shareTile({
      label: 'Shield accuracy',
      colored: true,
      isMaintenance: false,
      part: (kpis.falsePositiveShare.findings || 0) - (kpis.falsePositives.open || 0),
      total: kpis.falsePositiveShare.findings || 0,
      sub: (part, total) => {
        const fpTotal = kpis.falsePositives?.total || 0;
        const fpOpen = kpis.falsePositives?.open || 0;
        return `${part} of ${total} findings · ${kpis.openBugs || 0} real bugs open · ${fpTotal - fpOpen} false positives fixed` +
          (fpOpen > 0 ? ` · ${fpOpen} open` : '');
      },
      hint: 'No Shield findings in Jira yet',
      title: 'Shield findings in Jira (real bugs + false positives) minus the false positives still open, ÷ all findings, all time. Accuracy grows as false positives (shield-improvement) get fixed.',
    }),
    {
      label: 'Prompts Pass rate',
      value: formatPct(rates.pass),
      color: STATUS_COLORS.passed,
      pct: rates.pass,
    },
    {
      label: 'Prompts Fail rate',
      value: formatPct(rates.fail),
      color: STATUS_COLORS.fail,
      pct: rates.fail,
      badge: getBadRateBadge(rates.fail),
    },
    {
      label: 'Prompts Warning rate',
      value: formatPct(rates.warning),
      color: STATUS_COLORS.warning,
      pct: rates.warning,
      badge: getBadRateBadge(rates.warning),
    },
    ...(kpis ? [reviewedTile(kpis.reviewedPrompts, kpis.reviewVerdicts)] : []),
  ] : [];

  const alertsTiles = kpis && kpis.silentPrompts && kpis.falsePositives ? [
    {
      label: 'Silent prompts',
      color: (kpis.silentPrompts.prompts || 0) > 0 ? STATUS_COLORS.warning : STATUS_COLORS.passed,
      value: kpis.silentPrompts.prompts || 0,
      sub: `on ${kpis.silentPrompts.knownIssues || 0} known issue${(kpis.silentPrompts.knownIssues || 0) === 1 ? '' : 's'} · muted in Slack`,
      to: '/sense-shield/feedback-loop#known-issues',
      linkTitle: 'Manage known issues on the Feedback Loop page',
      badge: (kpis.silentPrompts.prompts || 0) > 0 ? 'Alert' : 'Good',
    },
    {
      label: 'Open bugs',
      value: kpis.openBugs || 0,
      color: (kpis.openBugs || 0) > 0 ? STATUS_COLORS.fail : STATUS_COLORS.passed,
      icon: (kpis.openBugs || 0) > 0 ? AlertCircle : undefined,
      href: OPEN_BUGS_JQL_URL,
      linkTitle: 'Open the shield-qa bugs in Jira',
      badge: (kpis.openBugs || 0) > 0 ? 'Critical' : 'Excellent',
    },
    {
      label: 'False positives',
      color: (kpis.falsePositives.open || 0) > 0 ? STATUS_COLORS.fail : STATUS_COLORS.passed,
      value: kpis.falsePositives.open || 0,
      sub: `open · ${(kpis.falsePositives.total || 0) - (kpis.falsePositives.open || 0)} of ${kpis.falsePositives.total || 0} fixed`,
      href: OPEN_FALSE_POSITIVES_JQL_URL,
      linkTitle: 'Open the open false positives (shield-improvement) in Jira',
      badge: (kpis.falsePositives.open || 0) > 0 ? 'Alert' : 'Excellent',
    },
  ] : [];

  const renderTile = (t) => {
    const pct = t.pct !== undefined ? t.pct : (t.part && t.total > 0 ? (t.part / t.total) * 100 : null);
    const badge = t.badge || getHealthBadge(pct);
    const badgeColor = badge === 'Critical' ? STATUS_COLORS.fail :
                       badge === 'Alert' ? STATUS_COLORS.warning :
                       badge === 'Excellent' ? STATUS_COLORS.passed : '#666';

    const body = (
      <>
        <div className="sense-shield-kpi-featured-header">
          <div className="sense-shield-kpi-label">
            {t.label}
            {t.isMaintenance && (
              <span className="sense-shield-kpi-maintenance-tag">
                <Wrench size={12} style={{ marginRight: 4 }} />
                Under Maintenance
              </span>
            )}
          </div>
          {badge && <div className="sense-shield-kpi-badge" style={{
            backgroundColor: badgeColor + '15',
            color: badgeColor
          }}>{badge}</div>}
        </div>
        <div className="sense-shield-kpi-value-featured" style={{ color: t.color }}>
          {t.icon && <t.icon size={24} style={{ marginRight: 8, color: t.color }} className="sense-shield-kpi-icon-alert" />}
          {t.value}
        </div>
        {t.sub && <div className="sense-shield-kpi-sub">{t.sub}</div>}
        {(t.value === '—' || t.value === '0') && t.hint && <div className="sense-shield-kpi-hint">{t.hint}</div>}
      </>
    );

    const topBarColor = t.color || '#0066cc';

    if (t.to) {
      return (
        <Link
          key={t.label}
          className="sense-shield-kpi-featured"
          to={t.to}
          title={t.linkTitle}
          style={{ borderTopColor: topBarColor }}
          aria-label={`${t.label}: ${t.value}. ${t.linkTitle}`}
        >
          {body}
          <ArrowRight size={16} className="sense-shield-kpi-featured-icon" aria-hidden="true" />
        </Link>
      );
    }

    return t.href ? (
      <a
        key={t.label}
        className="sense-shield-kpi-featured"
        href={t.href}
        target="_blank"
        rel="noreferrer"
        title={t.linkTitle}
        style={{ borderTopColor: topBarColor }}
        aria-label={`${t.label}: ${t.value}. ${t.linkTitle}`}
      >
        {body}
        <ExternalLink size={16} className="sense-shield-kpi-featured-icon" aria-hidden="true" />
      </a>
    ) : (
      <div
        key={t.label}
        className="sense-shield-kpi-featured"
        title={t.title}
        style={{ borderTopColor: topBarColor }}
      >
        {body}
      </div>
    );
  };

  return (
    <div className="sense-shield-kpi-container">
      {accuracyTiles.length > 0 && (
        <div className="sense-shield-kpi-section">
          <div className="sense-shield-kpi-section-header">
            <div className="sense-shield-kpi-section-title">Accuracy & Quality</div>
            <div className="sense-shield-kpi-section-subtitle">Core performance metrics</div>
          </div>
          <div className="sense-shield-kpi-row">
            {accuracyTiles.map(renderTile)}
          </div>
        </div>
      )}

      {alertsTiles.length > 0 && (
        <div className="sense-shield-kpi-section">
          <div className="sense-shield-kpi-section-header">
            <div className="sense-shield-kpi-section-title">Issues & Alerts</div>
            <div className="sense-shield-kpi-section-subtitle">Items requiring attention</div>
          </div>
          <div className="sense-shield-kpi-row">
            {alertsTiles.map(renderTile)}
          </div>
        </div>
      )}
    </div>
  );
}

export default KpiRow;
